/** TanStack Query hooks for the secondary-connectivity review queue + manual
 *  overrides (Phase 3 Stage 4). Mirrors the engineering/discrepancy hook shapes.
 *
 *  All reads degrade gracefully (empty page until a published run exists). The
 *  override mutations are app-authed (Bearer token via the shared client) and
 *  invalidate the review queue + override list + map/trace/detail caches so a
 *  saved decision is reflected immediately (read-time overlay). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acceptComponent,
  createOverride,
  deactivateOverride,
  getReviewQueue,
  listOverrides,
} from '../api/secondary.ts'
import type { ComponentAcceptRequest, OverrideCreate } from '../types/secondaryReview.ts'

export function useReviewQueue(params?: {
  feeder_id?: number | null
  skip?: number
  limit?: number
}) {
  const feederId = params?.feeder_id ?? undefined
  return useQuery({
    queryKey: ['secondary', 'review-queue', feederId, params?.skip, params?.limit],
    queryFn: () =>
      getReviewQueue({
        feeder_id: feederId,
        skip: params?.skip,
        limit: params?.limit,
      }),
    staleTime: 30_000,
  })
}

export function useOverrides(params?: { active_only?: boolean }) {
  return useQuery({
    queryKey: ['secondary', 'overrides', params?.active_only],
    queryFn: () => listOverrides({ active_only: params?.active_only }),
    staleTime: 30_000,
  })
}

/** Invalidate every cache the override overlay touches at read time. */
function useInvalidateOverrideConsumers() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['secondary', 'review-queue'] })
    queryClient.invalidateQueries({ queryKey: ['secondary', 'overrides'] })
    // Map color-by-confidence + detail panels read the same overlay.
    queryClient.invalidateQueries({ queryKey: ['map', 'secondary'] })
    queryClient.invalidateQueries({ queryKey: ['transformer-secondary'] })
    queryClient.invalidateQueries({ queryKey: ['meter-secondary-trace'] })
  }
}

export function useCreateOverride() {
  const invalidate = useInvalidateOverrideConsumers()
  return useMutation({
    mutationFn: (body: OverrideCreate) => createOverride(body),
    onSuccess: invalidate,
  })
}

export function useDeactivateOverride() {
  const invalidate = useInvalidateOverrideConsumers()
  return useMutation({
    mutationFn: (overrideId: number) => deactivateOverride(overrideId),
    onSuccess: invalidate,
  })
}

export function useAcceptComponent() {
  const invalidate = useInvalidateOverrideConsumers()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ComponentAcceptRequest) => acceptComponent(body),
    onSuccess: () => {
      invalidate()
      // Accept now writes meters.transformer_id: every meter/transformer
      // detail, the map's meter layer and the data-quality tab are stale.
      queryClient.invalidateQueries({ queryKey: ['meter'] })
      queryClient.invalidateQueries({ queryKey: ['transformer'] })
      queryClient.invalidateQueries({ queryKey: ['data-quality'] })
    },
  })
}
